import { useState, MouseEvent } from 'react';
import ResumeButtonIcon from '@mui/icons-material/ContactPage';
import { SvgIconProps } from '@mui/material';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { useSnackbar } from 'notistack';

interface Props extends SvgIconProps {
  applicationId?: number;
  name: string;
  showIcon?: boolean;
  userId?: number;
}

/**
 * A button that opens a resume in a new tab.
 *
 * If the applicationId is provided, attempt to open the resume from that application.
 * Otherwise, open the user's default resume.
 *
 */
export const ResumeButton = ({
  applicationId, name, showIcon = true, userId, ...props
}: Props) => {
  const API = useApi();
  const currentUser = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const handleResumeClick = async (e: MouseEvent) => {
    e.preventDefault();

    if (!currentUser) {
      enqueueSnackbar('Please log in to view resumes', { variant: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      // If we have a userId, try to get a signed URL for secure access
      if (userId) {
        const response = await API.getResumeSignedUrl(userId, applicationId);
        if (response?.signedUrl) {
          window.open(response.signedUrl, '_blank', 'noopener,noreferrer');
          return;
        }
      }
    } catch (error) {
      enqueueSnackbar('Unable to access resume', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <a
      href="#"
      onClick={handleResumeClick}
      download={`${name}_resume`}
      target="_blank"
      rel="noreferrer"
      className={isLoading ? 'opacity-50 cursor-not-allowed' : ''}
    >
      {showIcon ? (
        <ResumeButtonIcon {...props} />
      ) : (
        <button
          type="button"
          className="btn-primary-outlined"
          disabled={isLoading}
        >
          {isLoading ? 'Loading...' : 'Resume'}
        </button>
      )}
    </a>
  );
};
